import { Shift, User } from '@nextforce/api'
import { Button, Text, makeStyles } from '@rneui/themed'
import { t } from 'app/i18l'
import { font } from 'app/theme'
import { FlatList, View, useWindowDimensions } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import { CrewRating, CrewRatingMap } from '../queries/shared/types'
import { CrewRatingCard } from './crew_rating_card'

interface MultipleRatingsViewProps {
  shifts?: Shift[]
  rater: User | undefined
  onChangeCrewRating: (crewRating: CrewRating) => void
  onPressCloseButton(): void
  onSubmit(): Promise<void>
  crewRatings: CrewRatingMap
  error?: any
}

export function MultipleRatingsView({
  shifts,
  onChangeCrewRating,
  onPressCloseButton,
  onSubmit,
  rater,
  crewRatings,
  error,
}: MultipleRatingsViewProps) {
  const styles = useStyles()
  const { height } = useWindowDimensions()

  if (!rater) {
    return null
  }

  return (
    <SafeAreaView edges={['left', 'right', 'bottom']} style={styles.root}>
      <View style={styles.container}>
        <View style={styles.multipleShiftRatingContainer}>
          <View style={styles.headerContainer}>
            <View style={styles.leftAlignStyle}>
              <Text style={[styles.text, styles.name]}>{t('rateShifts')}</Text>
            </View>

            <View style={styles.leftAlignStyle}>
              <PressableOpacity
                style={styles.removeButton}
                onPress={onPressCloseButton}
              >
                <Text style={styles.removeButtonText}>X</Text>
              </PressableOpacity>
            </View>
          </View>
          <View style={{ flex: 1 }}>
            <FlatList
              data={shifts}
              renderItem={({ item }) => (
                <CrewRatingCard
                  shift={item}
                  onChangeCrewRating={onChangeCrewRating}
                  rater={rater}
                />
              )}
              keyExtractor={(shift: Shift) => `${shift.id}`}
              style={{ height: 0.5 * height }}
              contentContainerStyle={
                shifts?.length === 0 ? styles.contentContainerStyle : {}
              }
              ListEmptyComponent={() => (
                <View>
                  <Text style={styles.emptyListMessage}>
                    {t('noShiftsToRate')}
                  </Text>
                </View>
              )}
            />
          </View>
        </View>
        {error ? (
          <Text style={styles.errorText}>{error.message}</Text>
        ) : (
          <Button
            title={t('submit')}
            containerStyle={styles.buttonContainer}
            onPress={async () => {
              await onSubmit()
            }}
            disabled={Object.keys(crewRatings).length === 0}
          />
        )}
      </View>
    </SafeAreaView>
  )
}

const useStyles = makeStyles((theme, darkMode) => ({
  root: {
    flex: 1,
  },
  container: {
    flex: 1,
    backgroundColor: theme.colors?.white,
    borderTopWidth: 2,
    borderTopColor: theme.colors?.grey2,
  },
  multipleShiftRatingContainer: {
    flex: 1,
    backgroundColor: darkMode ? theme.colors?.grey6 : '#F2F2F2',
    borderTopWidth: 2,
    borderTopColor: theme.colors?.grey2,
  },
  text: {
    padding: 3,
    color: theme.colors.black,
  },
  name: {
    fontWeight: 'bold',
    fontSize: 16,
  },
  contentContainerStyle: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyListMessage: {
    fontSize: 24,
  },
  leftAlignStyle: {
    flexDirection: 'column',
    justifyContent: 'center',
    marginLeft: 'auto',
  },
  headerContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    backgroundColor: theme.colors.white,
    padding: 15,
  },
  removeButton: {
    marginRight: 20,
    backgroundColor: theme.colors?.grey3,
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  removeButtonText: {
    fontSize: 10,
    fontFamily: font.bold,
    color: theme.colors?.white,
  },
  buttonContainer: {
    margin: 10,
    paddingBottom: 10,
  },
  errorText: {
    textAlign: 'center',
    textAlignVertical: 'center',
    fontWeight: 'bold',
    color: theme.colors?.primary,
  },
}))
